import { Spin } from '@myua/kit';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { _urls } from '../../../../general/apiScenarios';
import { FileViewer } from '../../../../general/components/FullscreenPreview/FileViewer';
import { FullscreenPreview } from '../../../../general/components/FullscreenPreview/FullscreenPreview';
import {
  MediaType,
  ExtensionDefinition,
  getFileViewerLinkByDefinition,
} from '../../../../general/components/FullscreenPreview/model';
import { CompressedFileTreeProviderOptions } from '../../../../general/components/FullscreenPreview/viewers/common/compressed';
import { ImageMetadataProviderOptions } from '../../../../general/components/FullscreenPreview/viewers/common/image';
import * as layerActions from '../../../../general/components/FullscreenPreview/viewers/common/layer/actions';
import { LayerToolOptions } from '../../../../general/components/FullscreenPreview/viewers/common/layer/layerTool';
import {
  Asset,
  AssetPerm,
  isAssetDownloadAllowed,
  urlArchive,
  urlArchiveShare,
  urlAssetLayer,
  urlAssetLayerDownload,
  urlAssetLayerGroupDownload,
  urlAssetLayerView,
  urlCompressedTreeProvider,
} from '../../../api';
import { getAssetLabLogger } from '../../app/logs';
import { getMagicStudioEnabled, getUser } from '../../app/selectors';
import { routes } from '../../router/model';
import * as sharingActions from '../../sharing/actions';
import {
  getCanCreateSharingLink,
  getIsPublicDownload,
  getShareInProgress,
} from '../../sharing/selectors';
import { ViewMode } from '../../viewMode/model';
import { getViewMode } from '../../viewMode/selectors';
import { ErrorMessage } from './ErrorMessage';
import '../style.scss';
import { urlDirectDownload } from '../../../../general/urls';
import { DEMO_UNAVAILABLE_TOOLTIP, NodeFlag } from '../../../../general/model';
import { downloadDOM } from '../../../../general/utils/download';
import { getDefinition } from '../../../../general/components/FullscreenPreview/model';
import { useAutomaticViewerChoice } from '../../../../general/components/FullscreenPreview/useAutomaticViewerChoice';
import {
  applyViewerChoice,
  sanitizeViewer,
} from '../../../../general/components/FullscreenPreview/viewerChoice';
import { AutomaticViewerStatus } from '../../../../general/components/FullscreenPreview/AutomaticViewerStatus';
import { ViewerSwitch } from '../../../../general/components/FullscreenPreview/ViewerSwitch';
import { View3DButton } from './View3DButton';
import { FullscreenBasketButton } from '../../magicStudio/components/BasketButton';
import { CaptureMode } from '../../magicStudio/capture';

export const BasePreview: React.FC<{
  back: string;
  asset: Asset | null;
  error?: boolean | number;
  onNext?: () => void;
  onPrev?: () => void;
  originalView?: { isEnabled: boolean; enable: () => void };
}> = ({ back, asset, error, onNext, onPrev, originalView }) => {
  const assetsInShareProgress = useSelector(getShareInProgress).asset;
  const dispatch = useDispatch();
  const history = useHistory();
  const loc = useRouteMatch<{ assetID: string; sharingId?: string }>(back + routes.preview);
  const sharingId = loc?.params.sharingId;
  const viewMode = useSelector(getViewMode);
  const isCreative = viewMode === ViewMode.creatives;
  const isPublicDownload = useSelector(getIsPublicDownload);
  const assetLabLogger = useSelector(getAssetLabLogger);
  const user = useSelector(getUser);
  // Magic Studio is gated per instance (MYUA-16447) — see app/selectors.
  const magicStudioEnabled = useSelector(getMagicStudioEnabled);

  const onClose = useCallback(() => {
    history.replace(
      generatePath(back, {
        sharingId,
      }),
    );
  }, [history, back, sharingId]);

  useEffect(() => {
    const onKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const streamingLink = asset?.preview?.url ?? '';
  const downloadLink = asset?.download_url ?? '';

  const downloadAvailable = Boolean(isPublicDownload || asset?.perms.includes(AssetPerm.download));
  const onDownloadClicked =
    !isCreative && downloadAvailable && downloadLink
      ? () => {
          downloadDOM(downloadLink);
          assetLabLogger({
            action: 'Assets',
            sub_action: 'Download',
            extra: {
              // asset_ids: [navCurr.id],
              text_1: 'fullscreen',
            },
          });
        }
      : undefined;

  const canShare = useSelector(getCanCreateSharingLink);
  const shareAssetButtonCallback =
    canShare && !isCreative && asset
      ? () => {
          dispatch(sharingActions.shareAssetsClicked({ assetIds: [asset.id] }));
        }
      : null;
  // На демо-стенде (нет пермишена шаринга) кнопка остается видимой, но задизейбленной с тултипом.
  const shareAssetButtonDisabledTooltip =
    !canShare && !isCreative && asset ? DEMO_UNAVAILABLE_TOOLTIP : undefined;

  const layerTool: LayerToolOptions = {
    loadLayersUrl: urlAssetLayer(asset?.id ?? 0, sharingId),
    viewUrlFn: useCallback<LayerToolOptions['viewUrlFn']>(
      (fileName) => urlAssetLayerView(asset?.id ?? 0, fileName, sharingId),
      [asset, sharingId],
    ),
    downloadAvailable,
    downloadUrlFn: useCallback<LayerToolOptions['downloadUrlFn']>(
      (fileName) => urlAssetLayerDownload(asset?.id ?? 0, fileName, sharingId),
      [asset, sharingId],
    ),
    downloadGroupUrlFn: useCallback<LayerToolOptions['downloadGroupUrlFn']>(
      (layerId, isFullSize) =>
        urlAssetLayerGroupDownload(asset?.id ?? 0, layerId, isFullSize, sharingId),
      [asset, sharingId],
    ),
    downloadOnClick: useCallback<Required<LayerToolOptions>['downloadOnClick']>(
      ({ layerName, fileName, isGroup, isFullSize, url }) =>
        dispatch(
          layerActions.downloadClicked({
            log: {
              id: asset?.id ?? 0,
              name: asset?.name ?? '',
              layerName,
              fileName,
              isGroup,
              isFullSize,
              url,
            },
          }),
        ),
      [dispatch, asset],
    ),
    layersButtonOnClick: useCallback(
      () =>
        dispatch(
          layerActions.layersButtonClicked({
            log: {
              id: asset?.id ?? 0,
              name: asset?.name ?? '',
            },
          }),
        ),
      [dispatch, asset],
    ),
  };
  const compressedFileTreeProvider: CompressedFileTreeProviderOptions = {
    loadTreeURL: urlCompressedTreeProvider(asset?.id ?? 0, sharingId),
    fileURLGetter: (path: string) =>
      sharingId
        ? urlArchiveShare(asset?.id ?? 0, path, sharingId)
        : urlArchive(asset?.id ?? 0, path),
    // Архив с неодобренными лицензиями шрифтов: структуру смотреть можно, извлекать
    // файлы — нет (бэкенд ответит 403).
    openEntriesAllowed: asset ? isAssetDownloadAllowed(asset) : true,
  };
  const imageMetadataProvider: ImageMetadataProviderOptions = {
    loadURL: _urls.crhub.fs.metadata(asset?.id ?? 0),
  };

  // Выбор просмотрщика (Text / Spine), общий с сайдбаром (MYUA-17117). Хук вызывается безусловно.
  const {
    viewer,
    setViewer,
    status: viewerStatus,
    errorCode,
    retry,
  } = useAutomaticViewerChoice(
    asset && !isCreative ? getDefinition(asset.name) : undefined,
    asset?.download_url,
  );

  let headerExtra;
  let mediaDefinition: ExtensionDefinition | undefined;
  let hasLayerTool = false;

  if (!isCreative && asset) {
    const assetDefinition = getDefinition(asset.name);
    hasLayerTool = assetDefinition.hasLayerTool;

    if (assetDefinition.mt === MediaType.Asset3D) {
      // Управления переключением между preview и 3D может не быть, т.е. проп originalView отсутствует.
      // В таком случае сразу показываем 3D.
      // Такое может быть, например, если просмотр запущен не из пролистывания в каталоге, а из SinglePreview.
      if (asset.preview && originalView && !originalView.isEnabled) {
        mediaDefinition = getDefinition(`.${asset.preview.extension}`);
        headerExtra = (
          <View3DButton
            onClick={() => originalView.enable()}
            className="al-fullscreen-preview__original-button"
          />
        );
      } else {
        mediaDefinition = assetDefinition;
      }
    } else if (assetDefinition.alternativeViewers?.length) {
      const chosen = sanitizeViewer(assetDefinition, viewer);
      mediaDefinition = applyViewerChoice(assetDefinition, chosen);
      headerExtra = (
        <ViewerSwitch
          definition={assetDefinition}
          value={chosen}
          onChange={setViewer}
          className="al-fullscreen-preview__original-button"
        />
      );
    } else {
      if (asset.preview) {
        mediaDefinition = getDefinition(`.${asset.preview.extension}`);
      } else {
        mediaDefinition = assetDefinition;
      }
    }
  }

  const mediaType = isCreative ? MediaType.Video : mediaDefinition?.mt;

  // Magic Studio basket entry point in the fullscreen toolbar (non-creative
  // assets). For a static image it toggles the asset; for the video / 3D viewer
  // it captures the frame currently on screen and stages that as a reference.
  const captureMode: CaptureMode | null =
    mediaType === MediaType.Video ? 'video' : mediaType === MediaType.Asset3D ? '3d' : null;
  const toolbarExtra =
    magicStudioEnabled && !isCreative && asset ? (
      <FullscreenBasketButton asset={asset} captureMode={captureMode} />
    ) : null;
  const url = isCreative
    ? streamingLink
    : mediaDefinition
      ? getFileViewerLinkByDefinition(mediaDefinition, downloadLink, streamingLink)
      : '';

  const body = error ? (
    <ErrorMessage error={error} onClose={onClose} />
  ) : asset !== null ? (
    <FileViewer
      googleDriveProvider={{
        url: urlDirectDownload(asset),
      }}
      figmaImportProvider={{
        node: asset.flags.includes(NodeFlag.figma_file) ? asset : null,
      }}
      filename={asset.name}
      onNext={onNext}
      onPrev={onPrev}
      onClose={onClose}
      url={url}
      muted
      disableKeyboard
      onDownloadClicked={onDownloadClicked}
      copyLinkEnabled={false}
      copyLink={window.location.href}
      shareAssetButtonCallback={shareAssetButtonCallback}
      shareAssetButtonDisabledTooltip={shareAssetButtonDisabledTooltip}
      shareAssetInProgress={assetsInShareProgress[asset.id] === true}
      layerTool={hasLayerTool ? layerTool : undefined}
      compressedFileTreeProvider={compressedFileTreeProvider}
      // fs.metadata will return 401 if user is not authenticated
      imageMetadataProvider={user ? imageMetadataProvider : null}
      quickShareProvider={null}
      mediaContent={
        viewerStatus !== 'ready' ? (
          <AutomaticViewerStatus status={viewerStatus} errorCode={errorCode} onRetry={retry} />
        ) : undefined
      }
      mediaType={mediaType}
      headerExtra={headerExtra}
      toolbarExtra={toolbarExtra}
    />
  ) : (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Spin size="large" />
    </div>
  );

  return (
    <FullscreenPreview onClose={onClose}>
      <div className="al-fullscreen-preview">{body}</div>
    </FullscreenPreview>
  );
};
